import {
  authenticatedFetch,
  resolvePeriverseApiBaseUrl,
} from '@peritasai/data-access';
import {
  AssetEntity,
  assetRepository,
  LocationEntity,
  useAllLocations,
  useAndroid,
  ensureCachedLocations,
} from '@peritasai/repository';
import {
  AssetFilterParams,
  AssetLifecycleFunction,
  AssetType,
  LocationTypeName,
} from '@peritasai/types';
import { useCallback } from 'react';
import { z } from 'zod';

type ResetDiscoveryRequest = {
  hubId: string;
  scanId: string;
};

const PeritrackCommandResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});
type PeritrackCommandResponse = z.infer<typeof PeritrackCommandResponseSchema>;

type ResetDiscoveryResponse = {
  ok: boolean;
  status: number;
  data?: PeritrackCommandResponse;
};

export interface ReleaseToDeconService {
  getLocations: () => Promise<LocationEntity[]>;
  getTraysForLocationAndChild: (locationId: string) => Promise<AssetEntity[]>;
  resetDiscovery: (
    payload: ResetDiscoveryRequest
  ) => Promise<ResetDiscoveryResponse>;
}

export function useReleaseToDeconService(): ReleaseToDeconService {
  const { isAndroid, resetDiscovery: androidResetDiscovery } = useAndroid();
  const locationQuery = useAllLocations(); //Caches data

  const getLocations = useCallback(async (): Promise<LocationEntity[]> => {
    const searchLifecycle = AssetLifecycleFunction.CheckIn;
    const searchLocationType = LocationTypeName.STERILE_PROCESSING;

    const locationData = await ensureCachedLocations(locationQuery);

    return locationData
      .filter(
        (l) =>
          l.assetLifecycleFunction === searchLifecycle &&
          l.locationType === searchLocationType
      )
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      );
  }, [locationQuery]);

  const getTraysForLocation = useCallback(
    async (locationId: string): Promise<AssetEntity[]> => {
      const allFilters: AssetFilterParams = {
        type: AssetType.TRAY,
        locationId: locationId,
        includeAssignedCase: true,
        pageSize: 100,
      };
      const response = await assetRepository.getAssets(allFilters);
      return response;
    },
    []
  );

  const getTraysForLocationAndChild = useCallback(
    async (locationId: string): Promise<AssetEntity[]> => {
      //Walk Hierarchy
      const locationData = await ensureCachedLocations(locationQuery);
      const location = locationData.find((loc) => loc.id === locationId);
      if (location) {
        const results: AssetEntity[][] = await Promise.all(
          [location.id, ...location.children].map((locationId) =>
            getTraysForLocation(locationId)
          )
        );

        return results.flat();
      }
      return [];
    },
    [getTraysForLocation, locationQuery]
  );

  const resetDiscovery = useCallback(
    async (payload: ResetDiscoveryRequest): Promise<ResetDiscoveryResponse> => {
      if (isAndroid) {
        androidResetDiscovery();
        return { ok: true, status: 200 };
      } else {
        const baseUrl = resolvePeriverseApiBaseUrl();
        const response = await authenticatedFetch(
          `${baseUrl}/peritrack/control/reset-discovery`,
          {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          }
        );
        const raw = await response.json().catch(() => null);
        const parsed = PeritrackCommandResponseSchema.safeParse(raw);
        const data: PeritrackCommandResponse | undefined = parsed.success
          ? parsed.data
          : undefined;
        return { ok: response.ok, status: response.status, data };
      }
    },
    [androidResetDiscovery, isAndroid]
  );

  return {
    getLocations,
    getTraysForLocationAndChild,
    resetDiscovery,
  };
}
